import type { TFunction } from 'i18next';

/**
 * Pure helpers behind the recipe-detail content components. Kept out of the components so the
 * reconciliation rules between the vendor's data shape and the design's simpler mock — group-aware
 * column splitting, nutrition-row filtering, yield pluralization — are unit-testable on their own.
 */

export type RecipeTaxonomyField =
  | 'cuisine'
  | 'mealCourse'
  | 'dietaryRestrictions'
  | 'healthConsiderations'
  | 'mainIngredient'
  | 'preparationTechnique'
  | 'typeOfDish'
  | 'styleThemes'
  | 'seasonOccasion';

/** Mirrors the API's `IngredientGroup`: `name` is null for the vendor's unnamed/default group. */
export interface IngredientGroupInput {
  name: string | null;
  items: string[];
}

/** Mirrors the API's `NutritionFact`. `value` already carries the vendor's unit, e.g. "16 g". */
export interface NutritionFactInput {
  label: string;
  value: string;
}

/** The `shared`-namespace translator, so the ICU keys these helpers reach for stay type-checked. */
export type TranslateFn = TFunction<'shared'>;

/** Total ingredient count past which the list splits into two count-balanced columns. */
export const INGREDIENT_COLUMN_AT = 15;

/**
 * The twelve nutrition rows the story asks for, in the order it lists them. The API returns all
 * ~20-23 vendor rows unfiltered, so the page picks from them rather than the other way round.
 */
export const NUTRITION_FACT_LABELS = [
  'Calories',
  'Total Fat',
  'Cholesterol',
  'Total Carbohydrate',
  'Total Sugars',
  'Calcium',
  'Protein',
  'Saturated Fat',
  'Sodium',
  'Fiber',
  'Potassium',
  'Iron',
] as const;

/**
 * Lays the ingredient groups out in one or two columns. One column at or below
 * `INGREDIENT_COLUMN_AT` items; past it the list balances itself by item count, because a
 * sixteen-line column runs well past the preparation steps sitting beside it.
 *
 * Returns `[]` when there is nothing to render, so the caller can drop the section entirely.
 */
export function splitIngredientColumns(groups: IngredientGroupInput[]): IngredientGroupInput[][] {
  const populated = groups.filter((group) => group.items.length > 0);
  const total = populated.reduce((sum, group) => sum + group.items.length, 0);

  if (total === 0) return [];
  if (total <= INGREDIENT_COLUMN_AT) return [populated];

  // A lone group is the flat-list case: there is no group boundary to preserve, so it splits by
  // count the way the design's plain list does. The continuation half drops the heading instead of
  // repeating it, so a named group is still announced exactly once.
  if (populated.length === 1) {
    const only = populated[0];
    const half = Math.ceil(total / 2);
    return [[{ name: only.name, items: only.items.slice(0, half) }], [{ name: null, items: only.items.slice(half) }]];
  }

  // Several groups stay whole — a sub-heading orphaned from half its items reads as a data bug.
  // Split at whichever group boundary leaves the two sides closest in item count.
  let splitAt = 1;
  let smallestGap = Number.POSITIVE_INFINITY;
  let leading = 0;
  for (let index = 1; index < populated.length; index++) {
    leading += populated[index - 1].items.length;
    const gap = Math.abs(leading - (total - leading));
    if (gap < smallestGap) {
      smallestGap = gap;
      splitAt = index;
    }
  }

  return [populated.slice(0, splitAt), populated.slice(splitAt)];
}

/**
 * Narrows the vendor's unfiltered nutrition rows to `labels`, in `labels` order. Matching is
 * case-insensitive but exact; a label the recipe has no row for is left out rather than rendered
 * blank. The matched row keeps the vendor's own label text.
 */
export function filterNutritionFacts(facts: NutritionFactInput[], labels: readonly string[]): NutritionFactInput[] {
  const byLabel = new Map<string, NutritionFactInput>();
  for (const fact of facts) {
    const key = fact.label.toLowerCase();
    if (!byLabel.has(key)) byLabel.set(key, fact);
  }

  return labels
    .map((label) => byLabel.get(label.toLowerCase()))
    .filter((fact): fact is NutritionFactInput => fact !== undefined);
}

/** The yield cell's value: "1 serving" / "N servings". Empty when the recipe states no yield. */
export function formatServings(servings: number | null, t: TranslateFn): string {
  return servings === null ? '' : t('recipes.detail.yieldValue', { count: servings });
}

/** A taxonomy value's link into the Recipes listing, pre-filtered to that field's value. */
export function recipeTagHref(field: RecipeTaxonomyField, value: string): string {
  return `/resources/nutrition/recipes?${field}=${encodeURIComponent(value)}`;
}
